import { DayMap } from "@/modules/base_price/utils/dayDistribution";

/**
 * Prices one month day by day — the Fill & CPM calendar's arithmetic, and
 * the same rules the resolver applies (see MonthDayPricer.php): fill turns a
 * day's impressions into what it can sell, a hand-set CPM or budget ÷
 * sellable prices it, and the target's price bounds have the last word.
 */

export type DayPrice = {
	gross: number;
	budget: number;
	fill: number;
	sellable: number;
	/** What the day would price at from its budget alone. */
	derived: number;
	/** The price it actually gets. */
	cpm: number;
	revenue: number;
	/** Revenue against budget: positive earns more than it was given. */
	delta: number;
};

export type PriceBounds = { min: number | null; max: number | null };

export const clampToBounds = (cpm: number, bounds: PriceBounds): number => {
	if (bounds.min !== null && cpm < bounds.min) return bounds.min;
	if (bounds.max !== null && cpm > bounds.max) return bounds.max;

	return cpm;
};

export const priceDay = (
	gross: number,
	budget: number,
	fill: number,
	manualCpm: number | null,
	bounds: PriceBounds
): DayPrice => {
	const sellable = (gross * fill) / 100;
	const derived = sellable > 0 ? budget / (sellable / 1000) : 0;
	const raw = manualCpm !== null && manualCpm > 0 ? manualCpm : derived;
	const cpm = sellable > 0 ? clampToBounds(raw, bounds) : raw;
	const revenue = (cpm * sellable) / 1000;

	return { gross, budget, fill, sellable, derived, cpm, revenue, delta: revenue - budget };
};

/**
 * The fill that makes a day earn exactly its budget at `cpm` — what Hold
 * budget does when a price is typed. Capped at 100%: a price too low to reach
 * budget even with every impression sold takes them all and leaves the gap.
 */
export const solveFillForCpm = (gross: number, budget: number, cpm: number): number => {
	if (gross <= 0 || cpm <= 0) return 0;

	return Math.min(100, Math.max(0, ((budget / (cpm / 1000)) / gross) * 100));
};

/** Several days as one: fill weighted by impressions, CPM by what was sold. */
export const sumDays = (
	days: DayPrice[]
): { gross: number; sellable: number; budget: number; revenue: number; fill: number; cpm: number; delta: number } => {
	const gross = days.reduce((sum, day) => sum + day.gross, 0);
	const sellable = days.reduce((sum, day) => sum + day.sellable, 0);
	const budget = days.reduce((sum, day) => sum + day.budget, 0);
	const revenue = days.reduce((sum, day) => sum + day.revenue, 0);

	return {
		gross,
		sellable,
		budget,
		revenue,
		fill: gross > 0 ? (sellable / gross) * 100 : 0,
		cpm: sellable > 0 ? revenue / (sellable / 1000) : 0,
		delta: revenue - budget,
	};
};

/** Is revenue off budget by more than rounding? Same tolerance as the month view's per-month delta. */
export const isOffBudget = (delta: number, budget: number): boolean =>
	budget > 0 ? Math.abs(delta / budget) > 0.002 : Math.abs(delta) > 1;

export type DayPriceMap = DayMap<DayPrice>;
